import * as React from "react"
import { cn } from "@/lib/utils"

interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode
}

interface HeaderSectionProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

interface HeaderTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
  editable?: boolean
  value?: string
  onChange?: (value: string) => void
}

interface HeaderActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode
}

function Header({ className, children, ...props }: Readonly<HeaderProps>) {
  return (
    <header
      data-slot="header"
      className={cn(
        "fixed top-0 left-0 z-[1000] flex h-[60px] w-full items-center justify-between bg-background px-5 shadow-sm",
        className
      )}
      {...props}
    >
      {children}
    </header>
  )
}

function HeaderSection({
  className,
  children,
  ...props
}: Readonly<HeaderSectionProps>) {
  return (
    <div
      data-slot="header-section"
      className={cn("flex items-center gap-2", className)}
      {...props}
    >
      {children}
    </div>
  )
}

function HeaderTitle({
  className,
  children,
  editable = false,
  value,
  onChange,
  ...props
}: Readonly<HeaderTitleProps>) {
  if (editable && onChange) {
    return (
      <input
        data-slot="header-title"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "h-[30px] w-auto border-none bg-transparent px-1 text-lg font-bold text-primary outline-none",
          "font-['Plus_Jakarta_Sans',sans-serif]",
          className
        )}
        {...(props as React.InputHTMLAttributes<HTMLInputElement>)}
      />
    )
  }

  return (
    <div
      data-slot="header-title"
      className={cn(
        "text-lg font-bold text-primary",
        "font-['Plus_Jakarta_Sans',sans-serif]",
        className
      )}
      {...props}
    >
      {children ?? value}
    </div>
  )
}

function HeaderActions({
  className,
  children,
  ...props
}: Readonly<HeaderActionsProps>) {
  return (
    <div
      data-slot="header-actions"
      className={cn("flex items-center gap-2.5", className)}
      {...props}
    >
      {children}
    </div>
  )
}

export { Header, HeaderSection, HeaderTitle, HeaderActions }
